// ============================================
// Manor Miser - Room Generator
// ============================================
// Generates a random 12×12 room layout for the game board.
// Walls on all sides, a door on the top wall, a path from
// door to podium, player area in the lower half.

import {
  WALL_UPPER_LEFT, WALL_UPPER, WALL_UPPER_RIGHT,
  WALL_LEFT, WALL_RIGHT,
  WALL_LOWER_LEFT, WALL_LOWER, WALL_LOWER_RIGHT,
  DOOR_OPEN, PODIUM, FLOOR,
  PATH_BRICK_NS, PATH_BRICK_SE, PATH_BRICK_END,
  DECO_FLOOR, DECO_FURNITURE,
  SOLID_TILES,
} from './TileCatalog';
import type { TileCoord } from './Pathfinder';

export const MAP_W = 12;
export const MAP_H = 12;

export interface RoomLayout {
  /** 12×12 tile grid [row][col], 1-based tile IDs (0 = empty floor) */
  tiles: number[][];
  /** Per-tile rotation in radians [row][col] (0 = no rotation) */
  tileRotations: number[][];
  /** Walkable mask [row][col] — true = king/NPCs can walk here */
  walkable: boolean[][];
  /** Door tile position (on top wall) */
  door: TileCoord;
  /** Podium tile position */
  podium: TileCoord;
}

/**
 * Generate a random room layout.
 */
export function generateRoom(): RoomLayout {
  // Initialize with empty floor
  const tiles: number[][] = Array.from({ length: MAP_H }, () =>
    new Array(MAP_W).fill(FLOOR)
  );
  const tileRotations: number[][] = Array.from({ length: MAP_H }, () =>
    new Array(MAP_W).fill(0)
  );

  // 1. Place walls on all borders
  placeWalls(tiles);

  // 2. Place door on the top wall (random position, avoiding corners)
  const doorCol = randInt(2, MAP_W - 3); // cols 2..9
  tiles[0][doorCol] = DOOR_OPEN;
  const door: TileCoord = { col: doorCol, row: 0 };

  // 3. Place podium near center, upper half
  const podiumCol = randInt(4, 7);  // cols 4..7 (center-ish)
  const podiumRow = randInt(4, 5);  // rows 4..5
  tiles[podiumRow][podiumCol] = PODIUM;
  const podium: TileCoord = { col: podiumCol, row: podiumRow };

  // 4. Lay a path from the door down to the podium
  layPath(tiles, tileRotations, door, podium);

  // 5. Scatter decorations on empty floor (avoid player area rows 7-10)
  scatterDecorations(tiles);

  // 6. Build walkable mask
  const walkable = buildWalkable(tiles);

  // The door tile itself should be walkable (king enters through it)
  walkable[door.row][door.col] = true;
  // The row above the door (off-map) is treated as king spawn — pathfinder
  // handles start from row 0 since that's the door position.

  return { tiles, tileRotations, walkable, door, podium };
}

// ----------------------------------------
// Internal helpers
// ----------------------------------------

function placeWalls(tiles: number[][]): void {
  // Top wall
  tiles[0][0] = WALL_UPPER_LEFT;
  for (let c = 1; c < MAP_W - 1; c++) tiles[0][c] = WALL_UPPER;
  tiles[0][MAP_W - 1] = WALL_UPPER_RIGHT;

  // Bottom wall
  tiles[MAP_H - 1][0] = WALL_LOWER_LEFT;
  for (let c = 1; c < MAP_W - 1; c++) tiles[MAP_H - 1][c] = WALL_LOWER;
  tiles[MAP_H - 1][MAP_W - 1] = WALL_LOWER_RIGHT;

  // Side walls
  for (let r = 1; r < MAP_H - 1; r++) {
    tiles[r][0] = WALL_LEFT;
    tiles[r][MAP_W - 1] = WALL_RIGHT;
  }
}

function layPath(tiles: number[][], rotations: number[][], door: TileCoord, podium: TileCoord): void {
  const startRow = 1; // row just below the door (inside the room)
  const turnRow = podium.row - 1; // one row above podium

  // Build ordered list of path coordinates (vertical, then horizontal)
  const path: TileCoord[] = [];
  for (let r = startRow; r <= turnRow; r++) {
    path.push({ col: door.col, row: r });
  }
  if (door.col !== podium.col) {
    const dir = podium.col > door.col ? 1 : -1;
    for (let c = door.col + dir; ; c += dir) {
      path.push({ col: c, row: turnRow });
      if (c === podium.col) break;
    }
  }

  // For each cell determine tile type from neighbor connections
  for (let i = 0; i < path.length; i++) {
    const { col, row } = path[i];
    if (tiles[row][col] !== FLOOR) continue;

    const prev = i > 0 ? path[i - 1] : null;
    const next = i < path.length - 1 ? path[i + 1] : null;

    const n = (row === startRow && col === door.col) // door above
      || (prev?.col === col && prev?.row === row - 1)
      || (next?.col === col && next?.row === row - 1);
    const s = (prev?.col === col && prev?.row === row + 1)
      || (next?.col === col && next?.row === row + 1);
    const e = (prev?.row === row && prev?.col === col + 1)
      || (next?.row === row && next?.col === col + 1);
    const w = (prev?.row === row && prev?.col === col - 1)
      || (next?.row === row && next?.col === col - 1);

    let id: number;
    let rot = 0;

    if (n && s && !e && !w) {
      // Straight north-south
      id = PATH_BRICK_NS;
    } else if (e && w && !n && !s) {
      // Straight east-west (rotated N-S)
      id = PATH_BRICK_NS;
      rot = Math.PI / 2;
    } else if (n && e) {
      // Corner: north + east
      id = PATH_BRICK_SE;
      rot = Math.PI * 3 / 2; // 270° CW
    } else if (n && w) {
      // Corner: north + west
      id = PATH_BRICK_SE;
      rot = Math.PI; // 180°
    } else if (s && e) {
      // Corner: south + east (base orientation)
      id = PATH_BRICK_SE;
    } else if (s && w) {
      // Corner: south + west
      id = PATH_BRICK_SE;
      rot = Math.PI / 2; // 90° CW
    } else {
      // Dead end — one connection
      id = PATH_BRICK_END;
      // Base tile: opening on south side
      if (n) rot = Math.PI;          // opening faces north
      else if (s) rot = 0;           // opening faces south
      else if (e) rot = Math.PI * 3 / 2; // opening faces east
      else if (w) rot = Math.PI / 2; // opening faces west
    }

    tiles[row][col] = id;
    rotations[row][col] = rot;
  }
}

function scatterDecorations(tiles: number[][]): void {
  // Scatter grass on ~15% of empty floor tiles
  // In player rows 7-10, only allow decorations in edge columns (1-2, 10)
  for (let r = 1; r < MAP_H - 1; r++) {
    for (let c = 1; c < MAP_W - 1; c++) {
      if (tiles[r][c] !== FLOOR) continue;
      if (r >= 7 && r <= 10 && c >= 3 && c <= 9) continue; // player area center — keep clear

      if (Math.random() < 0.12) {
        tiles[r][c] = DECO_FLOOR[randInt(0, DECO_FLOOR.length - 1)];
      }
    }
  }

  // Place 1-2 furniture items in the upper half (rows 1-5), away from path
  let placed = 0;
  const maxFurniture = randInt(1, 2);
  for (let attempt = 0; attempt < 30 && placed < maxFurniture; attempt++) {
    const r = randInt(1, 5);
    const c = randInt(1, MAP_W - 2);
    if (tiles[r][c] === FLOOR) {
      tiles[r][c] = DECO_FURNITURE[randInt(0, DECO_FURNITURE.length - 1)];
      placed++;
    }
  }
}

function buildWalkable(tiles: number[][]): boolean[][] {
  return tiles.map(row =>
    row.map(tileId => {
      if (tileId === FLOOR) return true;
      if (SOLID_TILES.has(tileId)) return false;
      // Path tiles, grass, etc. are walkable
      return true;
    })
  );
}

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
